import axios, { AxiosInstance } from "axios";

import { parseNumericCsv } from "./CsvParsing";

export interface FetchAthenaResultOptions {
  onStatusUpdate: (status: string) => void;
  onDownloadComplete?: (totalSizeInKB: number) => void;
  maximumDownloadSizeInKB?: number;
}

export interface AthenaResultsSource {
  url: string;
  headUrl: string;
}

export interface AthenaResults {
  url: string;
  parsedRows: number[][];
  rawRows: string[][];
  columns: AthenaResultsColumn[];
  error: unknown;
}

export interface AthenaResultsColumn {
  name: string;
  index: number;
}
export class MaximumDownloadSizeExceededError extends Error {}
export const downloadData = async (
  url: string,
  headUrl: string,
  maximumDownloadSizeInKB: number | undefined,
  onProgress: (totalDownloadedInKB: number, totalSizeInKB: number) => void,
  axios_get: AxiosInstance["get"],
  axios_head: AxiosInstance["head"]
) => {
  let totalSizeInKB = 0;
  try {
    const csvInfo = await axios_head(headUrl, {});
    totalSizeInKB = csvInfo.headers["content-length"] / 1024;
    if (maximumDownloadSizeInKB && totalSizeInKB > maximumDownloadSizeInKB)
      return {
        error: new MaximumDownloadSizeExceededError(
          `Data download size of ${totalSizeInKB.toFixed(
            0
          )}KB exceeds ${maximumDownloadSizeInKB.toFixed(0)}KB.`
        ),
        totalSizeInKB,
        response: undefined,
      };
    onProgress(0, totalSizeInKB);
    const response = await axios_get(url, {
      maxContentLength: 1024 * 1204 * 10,
      onDownloadProgress: function (progressEvent) {
        onProgress(progressEvent.loaded / 1024, totalSizeInKB);
      },
    });
    return { response, totalSizeInKB };
  } catch (ex) {
    return { error: ex, totalSizeInKB: 0, response: undefined };
  }
};

export const fetchAthenaResults = async (
  athenaResults: AthenaResultsSource,
  {
    maximumDownloadSizeInKB,
    onStatusUpdate,
    onDownloadComplete,
  }: FetchAthenaResultOptions
): Promise<AthenaResults> => {
  onStatusUpdate(`Downloading results...`);
  const { response, error: responseError, totalSizeInKB } = await downloadData(
    athenaResults.url,
    athenaResults.headUrl,
    maximumDownloadSizeInKB,
    (totalDownloadedInKB, totalSizeInKB) => {
      onStatusUpdate(
        `Downloading ${totalDownloadedInKB.toFixed(
          0
        )}KB of ${totalSizeInKB.toFixed(0)}KB of results...`
      );
    },
    axios.get,
    axios.head
  );
  onDownloadComplete && onDownloadComplete(totalSizeInKB);
  if (responseError) {
    onStatusUpdate(`Warning: ${responseError.message}`);
    console.warn(responseError);
    return {
      url: athenaResults.url,
      error: responseError,
      parsedRows: [],
      rawRows: [],
      columns: [],
    };
  }
  // Parse local CSV file
  onStatusUpdate("Parsing CSV...");
  const { parsedRows, rawRows, error, columnNames } = parseNumericCsv(
    response!.data
  );
  if (error) {
    onStatusUpdate(error);
  }
  return {
    url: athenaResults.url,
    parsedRows,
    rawRows,
    columns: columnNames.map((name, index) => ({ name, index })),
    error,
  };
};
